import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ExternalLink, Globe2, Loader2, Mail, MessageSquare, Quote, Sparkles, Video } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  supabase,
  HowieCompanionMessage,
  HowieCompanionReport,
  HowieCompanionSession,
  HowieCompanionSnippet,
  HowieCompanionSurface,
} from '../services/supabase';
import {
  companionDesktopLabel,
  companionSnippetSourceLabel,
  isDesktopCompanionSession,
  isMeetingTake,
  isOpenableCompanionUrl,
} from '../lib/companionLabels';

type LoadStatus = 'loading' | 'ready' | 'error';

const SURFACE_LABELS: Record<HowieCompanionSurface, string> = {
  generic: 'Browser',
  meet: 'Google Meet',
  gmail: 'Gmail',
};
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function SurfaceIcon({ session }: { session: HowieCompanionSession }) {
  if (session.surface === 'meet' || isDesktopCompanionSession(session)) return <Video size={16} />;
  if (session.surface === 'gmail') return <Mail size={16} />;
  return <Globe2 size={16} />;
}

function surfaceLabel(session: HowieCompanionSession) {
  return companionDesktopLabel(session) ?? SURFACE_LABELS[session.surface];
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Unknown';
  try {
    return format(new Date(value), 'MMM d, yyyy h:mm a');
  } catch {
    return 'Unknown';
  }
}

function companionTitle(session: HowieCompanionSession) {
  return session.title || session.gmail_subject || session.meeting_code || 'HOWIE Companion Session';
}

function statusLabel(session: HowieCompanionSession) {
  if (session.status === 'report_ready') return 'Report ready';
  if (session.status === 'ended') return 'Ended';
  return 'Active';
}

export default function CompanionSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionRecord, setSessionRecord] = useState<HowieCompanionSession | null>(null);
  const [messages, setMessages] = useState<HowieCompanionMessage[]>([]);
  const [snippets, setSnippets] = useState<HowieCompanionSnippet[]>([]);
  const [report, setReport] = useState<HowieCompanionReport | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadCompanionSession = async () => {
      if (!sessionId || !UUID_PATTERN.test(sessionId) || !user) {
        setLoadStatus('error');
        setErrorMessage('Companion session not found.');
        return;
      }

      setLoadStatus('loading');
      setErrorMessage(null);

      try {
        const [sessionResponse, messagesResponse, snippetsResponse, reportResponse] = await Promise.all([
          supabase
            .from('howie_companion_sessions')
            .select('id,user_id,surface,title,url,status,meeting_code,gmail_subject,last_advice,report_status,started_at,ended_at,created_at,updated_at,metadata')
            .eq('id', sessionId)
            .eq('user_id', user.id)
            .limit(1),
          supabase
            .from('howie_companion_messages')
            .select('id,session_id,user_id,role,content,card,created_at,metadata')
            .eq('session_id', sessionId)
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })
            .limit(200),
          supabase
            .from('howie_companion_snippets')
            .select('id,session_id,user_id,label,text,source,created_at,metadata')
            .eq('session_id', sessionId)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(80),
          supabase
            .from('howie_companion_reports')
            .select('id,session_id,user_id,status,title,body_markdown,scorecard,follow_up,email_sent_at,created_at,updated_at,metadata')
            .eq('session_id', sessionId)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1),
        ]);

        if (sessionResponse.error) throw sessionResponse.error;
        if (messagesResponse.error) throw messagesResponse.error;
        if (snippetsResponse.error) throw snippetsResponse.error;
        if (reportResponse.error) throw reportResponse.error;

        const sessionRows = (sessionResponse.data || []) as HowieCompanionSession[];
        const loadedSession = sessionRows[0];
        if (!loadedSession) {
          throw new Error('Companion session not found.');
        }

        if (!cancelled) {
          setSessionRecord(loadedSession);
          setMessages((messagesResponse.data || []) as HowieCompanionMessage[]);
          setSnippets((snippetsResponse.data || []) as HowieCompanionSnippet[]);
          setReport(((reportResponse.data || []) as HowieCompanionReport[])[0] || null);
          setLoadStatus('ready');
        }
      } catch (error) {
        console.error('[Howie] Error loading companion session:', error);
        if (!cancelled) {
          setLoadStatus('error');
          setErrorMessage(error instanceof Error ? error.message : 'Unable to load companion session.');
        }
      }
    };

    loadCompanionSession();

    return () => {
      cancelled = true;
    };
  }, [sessionId, user]);

  const recentSnippets = useMemo(() => snippets.slice(0, 12), [snippets]);

  if (loadStatus === 'loading') {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-how-accent-warm" />
      </div>
    );
  }

  if (loadStatus === 'error' || !sessionRecord) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white px-6">
        <div className="max-w-md text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-200 mb-4" />
          <h1 className="text-2xl font-heading font-bold tracking-normal text-how-space-deep">Session unavailable</h1>
          <p className="mt-2 text-sm text-gray-500">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-y-auto bg-white">
      <div className="max-w-6xl mx-auto px-5 py-6 md:px-8 md:py-8">
        <header className="pb-6 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-2 text-xs font-heading font-bold text-how-space-mid">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-how-space-deep/10 px-3 py-1">
              <SurfaceIcon session={sessionRecord} />
              {surfaceLabel(sessionRecord)}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-500">
              {statusLabel(sessionRecord)}
            </span>
            {sessionRecord.report_status === 'ready' && (
              <span className="rounded-full bg-how-accent-warm/10 px-3 py-1 text-how-space-mid">
                Report available
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <h1 className="text-3xl md:text-4xl font-heading font-black tracking-normal text-how-space-deep">
                {companionTitle(sessionRecord)}
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Last updated {formatDateTime(sessionRecord.updated_at)}
              </p>
            </div>

            {isOpenableCompanionUrl(sessionRecord.url) && (
              <a
                href={sessionRecord.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-heading font-bold text-how-space-mid hover:bg-gray-50"
              >
                <ExternalLink size={16} />
                Open Source
              </a>
            )}
          </div>
        </header>

        <section className="grid gap-3 py-5 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-100 p-4">
            <p className="text-xs font-heading font-bold uppercase text-gray-400">Started</p>
            <p className="mt-1 text-sm text-how-space-mid">{formatDateTime(sessionRecord.started_at)}</p>
          </div>
          <div className="rounded-lg border border-gray-100 p-4">
            <p className="text-xs font-heading font-bold uppercase text-gray-400">Surface</p>
            <p className="mt-1 text-sm text-how-space-mid">{surfaceLabel(sessionRecord)}</p>
          </div>
          <div className="rounded-lg border border-gray-100 p-4">
            <p className="text-xs font-heading font-bold uppercase text-gray-400">Retained Context</p>
            <p className="mt-1 text-sm text-how-space-mid">{snippets.length} snippets</p>
          </div>
        </section>

        <div className="grid gap-6 pb-8 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-how-accent-warm" />
              <h2 className="text-xl font-heading font-bold tracking-normal text-how-space-deep">Advice History</h2>
            </div>

            {messages.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">
                No messages recorded.
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <article
                    key={message.id}
                    className={`rounded-lg border p-4 ${
                      message.role === 'assistant'
                        ? 'border-how-accent-warm/25 bg-how-accent-warm/5'
                        : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-heading font-bold uppercase text-gray-400">
                        {message.role === 'assistant' ? 'HOWIE' : 'You'}
                      </p>
                      <p className="text-xs text-gray-400">{formatDateTime(message.created_at)}</p>
                    </div>

                    {message.card ? (
                      <div className="mt-3 space-y-3">
                        <div>
                          <h3 className="text-lg font-heading font-bold tracking-normal text-how-space-deep">
                            {message.card.title || 'HOWIE move'}
                          </h3>
                          <p className="mt-1 text-sm leading-6 text-how-space-mid">
                            {message.card.nudge || message.content}
                          </p>
                        </div>

                        {message.card.suggestedWords && (
                          <blockquote className="rounded-lg bg-white p-3 text-sm leading-6 text-how-space-mid border border-how-accent-warm/20">
                            {message.card.suggestedWords}
                          </blockquote>
                        )}

                        {(message.card.howLens || message.card.why) && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            {message.card.howLens && (
                              <div className="rounded-lg bg-white/70 p-3">
                                <p className="text-xs font-heading font-bold uppercase text-gray-400">HOW Lens</p>
                                <p className="mt-1 text-sm text-how-space-mid">{message.card.howLens}</p>
                              </div>
                            )}
                            {message.card.why && (
                              <div className="rounded-lg bg-white/70 p-3">
                                <p className="text-xs font-heading font-bold uppercase text-gray-400">Why</p>
                                <p className="mt-1 text-sm text-how-space-mid">{message.card.why}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm leading-6 text-how-space-mid">{message.content}</p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <aside className="space-y-6">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Quote size={18} className="text-how-accent-warm" />
                <h2 className="text-xl font-heading font-bold tracking-normal text-how-space-deep">Context</h2>
              </div>
              {recentSnippets.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-400">
                  No retained snippets.
                </div>
              ) : (
                <div className="space-y-3">
                  {recentSnippets.map((snippet) => (
                    <article key={snippet.id} className="rounded-lg border border-gray-100 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-xs font-heading font-bold uppercase text-gray-400">
                          {snippet.label}
                        </p>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                          {companionSnippetSourceLabel(snippet)}
                        </span>
                      </div>
                      <p className="mt-2 line-clamp-5 text-sm leading-6 text-how-space-mid">
                        {snippet.text}
                      </p>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {report && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={18} className="text-how-accent-warm" />
                  <h2 className="text-xl font-heading font-bold tracking-normal text-how-space-deep">
                    {isMeetingTake(report) ? 'Meeting take' : 'Preliminary Report'}
                  </h2>
                </div>
                <article className="rounded-lg border border-gray-100 p-4">
                  <h3 className="text-base font-heading font-bold tracking-normal text-how-space-deep">
                    {report.title}
                  </h3>
                  <div className="prose prose-sm max-w-none mt-3 text-how-space-mid [&_h1]:tracking-normal [&_h2]:tracking-normal [&_h3]:tracking-normal">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (props) => <h4 className="text-base font-heading font-bold tracking-normal text-how-space-deep" {...props} />,
                        h2: (props) => <h5 className="text-sm font-heading font-bold tracking-normal text-how-space-deep" {...props} />,
                        h3: (props) => <h6 className="text-sm font-heading font-bold tracking-normal text-how-space-deep" {...props} />,
                      }}
                    >
                      {report.body_markdown}
                    </ReactMarkdown>
                  </div>
                </article>
              </section>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
